import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Navigation from '../components/Navigation';
import Logo from '../components/Logo';

export function ProfitabilityContent() {
  const [tab, setTab] = useState('gpo');
  const [gpoProfit, setGpoProfit] = useState([]);
  const [gpoTotals, setGpoTotals] = useState({});
  const [expandedGpo, setExpandedGpo] = useState(null);
  const [summary, setSummary] = useState({});
  const [orders, setOrders] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [costStructure, setCostStructure] = useState({});
  const [loading, setLoading] = useState(true);
  const [showEditCost, setShowEditCost] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [sumRes, ordRes, buyRes, costRes, gpoRes] = await Promise.all([
        api.get('/profitability/summary'),
        api.get('/profitability/orders'),
        api.get('/profitability/buyers'),
        api.get('/profitability/cost-structure'),
        api.get('/profitability/gpo-profitability').catch(() => ({ data: { data: [], totals: {} } }))
      ]);
      setSummary(sumRes.data.data || {});
      setOrders(ordRes.data.data || []);
      setBuyers(buyRes.data.data || []);
      setCostStructure(costRes.data.data || {});
      setGpoProfit(gpoRes.data.data || []);
      setGpoTotals(gpoRes.data.totals || {});
    } catch (err) {
      console.error('Profitability fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCost = async (e) => {
    e.preventDefault();
    // ── A KEYED COST OVERRIDES THE DERIVED ONE, SO IT NEEDS A REASON (ruling 31) ─────────────
    // Mirrored from the route, which refuses the same thing — and from the mig-333 CHECK under
    // that. Answered here so the clerk is not sent to the server to be told.
    const total = ['fabric_cost','trim_cost','cmt_cost','overhead_cost','freight_cost','rejection_loss']
      .reduce((t, k) => t + (parseFloat(form[k]) || 0), 0);
    if (total > 0 && !String(form.override_reason || '').trim()) {
      setError('Say why the derived cost is wrong for this order — a keyed figure replaces it, so the reason goes on the record beside the number.');
      return;
    }
    setSaving(true); setError('');
    try {
      await api.patch(`/profitability/cost-sheet/${selectedOrder.id}`, form);
      setShowEditCost(false); setForm({});
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update cost sheet');
    } finally { setSaving(false); }
  };

  const marginBadge = (pct) => {
    const p = parseFloat(pct);
    // A NULL margin is not a bad margin. Without this the red band claimed every uncosted order
    // was below 15% — the mirror image of the 100% it used to claim.
    if (!Number.isFinite(p)) return { bg: '#F4F4F2', color: '#999' };
    if (p >= 25) return { bg: '#EEEDF8', color: '#2A2970' };
    if (p >= 15) return { bg: '#FAEEDA', color: '#854F0B' };
    return { bg: '#FCEBEB', color: '#A32D2D' };
  };

  const statusBadge = (status) => {
    const map = {
      shipped:       { bg: '#EEEDF8', color: '#2A2970' },
      in_production: { bg: '#FAEEDA', color: '#854F0B' },
      confirmed:     { bg: '#EEEDF8', color: '#2A2970' },
      complete:      { bg: '#EEEDF8', color: '#2A2970' },
      sample:        { bg: '#F1EFE8', color: '#5F5E5A' },
    };
    const s = map[status] || { bg: '#F1EFE8', color: '#5F5E5A' };
    return (
      <span style={{ ...s, padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 500 }}>
        {status && status.replace('_', ' ')}
      </span>
    );
  };

  const fmt = (n) => '₹' + parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  // ── A FIGURE WITH NO INPUT BEHIND IT RENDERS BLANK (rulings 30 and 33) ───────────────────
  // fmt() above sends null through parseFloat(n || 0) and prints ₹0 — which is how "nobody has
  // costed this order" reached the screen as a cost, and a 100% margin with it. Anything that
  // can legitimately be absent goes through fmtOrBlank instead. fmt() is kept for revenue and
  // for figures that are always present.
  const DASH = <span style={{ color: '#BBB' }} title="no figure recorded — this is not zero">—</span>;
  const fmtOrBlank = (n) => (n === null || n === undefined || n === '') ? DASH : fmt(n);
  const pctOrBlank = (n) => (n === null || n === undefined || n === '') ? DASH : parseFloat(n).toFixed(2) + '%';

  // What kind of cost number a row carries (ruling 30). Three values, and the two absent ones are
  // NOT merged: 59 sheets were opened and left blank, 75 were never opened, and those are facts
  // about different people.
  const BASIS = {
    costed:            { label: 'keyed',      color: '#7A2E8A', bg: '#F5EAF7', tip: 'A cost was keyed by hand for this order, and a keyed figure OVERRIDES the derived one. Whoever keyed it had to say why — hover the reason beside it.' },
    derived_complete:  { label: 'derived',    color: '#2A2970', bg: '#EEEDF8', tip: 'Cost derived from this order\'s own purchase orders (excluding Deroi buyouts) and certified job work, with labour and overhead actuals. Nothing is missing from it.' },
    derived_partial:   { label: 'part-derived', color: '#854F0B', bg: '#FAEEDA', tip: 'Cost derived from what exists, but some components have no figure yet. A partial cost UNDERSTATES, which overstates the margin — the missing pieces are named beside it.' },
    derived_none:      { label: 'no cost',    color: '#8A6B00', bg: '#FCF3E0', tip: 'Nothing was purchased against this order and no cost was keyed, so there is nothing to derive a cost from. This is not a zero cost.' },
    // Reported as sheet_state on a derived_none row, so the two absent cases stay apart.
    cost_sheet_empty:  { label: 'sheet blank', color: '#8A6B00', bg: '#FCF3E0', tip: 'A cost sheet exists for this order but every column is 0.00 — created and never filled in.' },
    no_cost_sheet:     { label: 'no sheet',   color: '#8A6B00', bg: '#FCF3E0', tip: 'No cost sheet exists for this order either.' },
  };
  const basisChip = (b, row) => {
    const d = BASIS[b] || { label: b || 'unknown', color: '#666', bg: '#F0F0F0', tip: '' };
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
        <span title={d.tip} style={{ background: d.bg, color: d.color, padding: '2px 7px', borderRadius: 9, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>{d.label}</span>
        {/* WHAT IS MISSING, BY NAME (ruling 31). A partial figure may be shown but never as a
            whole one, so the gap is on the row rather than in a tooltip nobody opens. */}
        {b === 'derived_partial' && !!(row?.cogs_missing || []).length &&
          <span style={{ fontSize: 10, color: '#854F0B' }} title={'No figure yet for: ' + row.cogs_missing.join(', ')}>
            −{row.cogs_missing.join(', ')}
          </span>}
        {b === 'costed' && row?.override_reason &&
          <span style={{ fontSize: 10, color: '#7A2E8A', cursor: 'help' }} title={'Reason given: ' + row.override_reason}>why?</span>}
        {b === 'derived_none' && row?.sheet_state &&
          <span style={{ fontSize: 10, color: '#9a8a5a' }} title={BASIS[row.sheet_state]?.tip || ''}>
            {BASIS[row.sheet_state]?.label}
          </span>}
      </span>
    );
  };

  const inputStyle = {
    width: '100%', padding: '8px 10px', border: '1px solid #ddd',
    borderRadius: 6, fontSize: 13, boxSizing: 'border-box', marginTop: 4
  };
  const labelStyle = {
    fontSize: 12, fontWeight: 500, color: '#555', display: 'block', marginBottom: 8
  };


  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading Profitability...</div>;

  return (
    <>
      <div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
          {[
            // QUOTED REVENUE COVERS ONLY THE GPOs THAT DECLARE ONE (ruling 33). 61 of 138 have no
            // live estimate; their revenue is blank, not zero, and the tile says how many it
            // could add up rather than presenting a partial total as a complete one.
            { label: 'Quoted Revenue', value: fmt(gpoTotals.revenue), color: '#3B3A8F',
              note: Number(gpoTotals.revenue_undeclared) ? `${gpoTotals.revenue_rows} of ${Number(gpoTotals.revenue_rows) + Number(gpoTotals.revenue_undeclared)} GPOs declare one` : null },
            { label: 'Est Cost', value: fmt(gpoTotals.est), color: '#854F0B' },
            { label: 'Actual Direct Cost', value: fmt(gpoTotals.actual), color: '#E24B4A',
              note: 'fabric/yarn, trims, job work only' },
            { label: 'Variance', value: (gpoTotals.variance > 0 ? '+' : '') + fmt(gpoTotals.variance), color: gpoTotals.variance > 0 ? '#E22D2D' : '#166534' },
            { label: 'Contribution %', value: gpoTotals.margin_pct == null ? '—' : Number(gpoTotals.margin_pct).toFixed(1) + '%', color: Number(gpoTotals.margin_pct) >= 20 ? '#166534' : Number(gpoTotals.margin_pct) >= 0 ? '#854F0B' : '#E22D2D' },
          ].map((kpi, i) => (
            <div key={i} style={{ background: 'white', borderRadius: 10, padding: '16px 20px', boxShadow: 'none', border: '1px solid #E8E7E7' }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>{kpi.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
              {kpi.note && <div style={{ fontSize: 11, color: '#8A6B00', marginTop: 4 }}>{kpi.note}</div>}
            </div>
          ))}
        </div>

        {/* WHAT THESE FIGURES COVER, ABOVE THE FIGURES (ruling 30). Every cost total on this page
            sums COSTED ORDERS ONLY. Before this the page showed Rs 0 COGS and a 100% margin over
            122 orders, none of which had a recorded cost — a caveat in a column comment is one
            nobody reading the number ever sees, so it goes here. */}
        {!!Number(summary.uncosted_orders) && (
          <div style={{ marginBottom: 16, border: '1px solid #f6d195', background: '#FFFDF5', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: '#6b5a2e', lineHeight: 1.6 }}>
            <strong style={{ color: '#854F0B' }}>
              These figures are CONTRIBUTION AFTER DIRECT COST, not gross margin.
            </strong>{' '}
            Direct cost is derived from each order's own purchase orders (Deroi buyouts excluded)
            and its certified job work. <strong>Labour, factory overhead, freight and rejection loss
            are not in it</strong> — the Cost Structure tab names why each is unavailable, so a true
            gross margin would be lower than what is shown here. A hand-keyed cost sheet overrides
            the derived figure, and each row says which was used.
            {' '}<strong>{summary.costed_orders ?? 0} of {summary.total_orders ?? 0}</strong> orders
            have a direct cost; the remaining {summary.uncosted_orders} show{' '}
            <span style={{ color: '#BBB', fontWeight: 700 }}>—</span> rather than ₹0, because no cost
            recorded is not a cost of nothing.
            {' '}({summary.orders_derived_complete ?? 0} fully derived,
            {' '}{summary.orders_derived_partial ?? 0} part-derived,
            {' '}{summary.orders_keyed ?? 0} hand-keyed,
            {' '}{summary.orders_derived_none ?? 0} with nothing purchased against them — of which
            {' '}{summary.orders_cost_sheet_empty ?? 0} have a blank cost sheet and
            {' '}{summary.orders_no_cost_sheet ?? 0} have none.)
            {!!Number(summary.excluded_gpos_no_order_row) && <>
              {' '}A further <strong>{summary.excluded_gpos_no_order_row} GPO(s)</strong> are absent from this
              view entirely for want of a companion order row.
            </>}
          </div>
        )}

        <div style={{ background: 'white', borderRadius: 10, boxShadow: 'none', border: '1px solid #E8E7E7', overflow: 'hidden' }}>
          <div style={{ display: 'flex', borderBottom: '1px solid #eee', padding: '0 20px' }}>
            {[
              { key: 'gpo', label: '📊 GPO Profitability' },
              { key: 'overview', label: 'Order P&L' },
              { key: 'buyers', label: 'By Buyer' },
              { key: 'costs', label: 'Cost Structure' },
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                padding: '14px 20px', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: tab === t.key ? 600 : 400,
                color: tab === t.key ? '#3B3A8F' : '#888',
                borderBottom: tab === t.key ? '2px solid #1D5EA8' : '2px solid transparent',
                marginBottom: -1
              }}>{t.label}</button>
            ))}
          </div>

          <div style={{ padding: 20 }}>

            {tab === 'gpo' && (() => {
              const inr = (v) => '₹' + Math.round(Number(v) || 0).toLocaleString('en-IN');
              const marginColor = (m) => m == null ? '#aaa' : m >= 20 ? '#166534' : m >= 0 ? '#854F0B' : '#E22D2D';
              const varColor = (v) => v > 0 ? '#E22D2D' : v < 0 ? '#166534' : '#888';
              return (
                <div>
                  <div style={{ marginBottom: 14, padding: '10px 14px', background: '#EEEDF8', borderRadius: 6, fontSize: 12, color: '#3B3A8F' }}>
                    Estimated cost (from Cost Estimation × order qty) vs actual PO cost raised. Labour &amp; overhead use the estimate (not procured). Click a row for the cost breakdown.
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead><tr style={{ background: '#F7F6F3' }}>
                      {['GPO No', 'Style', 'Buyer / Brand', 'Qty', 'Est Cost', 'Actual Cost', 'Variance', 'Margin %', ''].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '10px 12px', color: '#888', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {gpoProfit.length === 0 ? <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No GPOs yet.</td></tr>
                        : gpoProfit.map((r, i) => {
                          const open = expandedGpo === r.gpo_no;
                          // ── WHICH KIND OF NUMBER THE JOB-WORK ACTUAL IS (ruling 26b) ──────────
                          // A certified figure and an estimate are identical as numbers, and that
                          // identity hid a wrong claim about this column. The row says which it is,
                          // and the column header says the actuals are certified where they can be.
                          const JW_BASIS = {
                            certified: { label: 'certified', c: '#166534', t: 'Finance certified this job-work cost.' },
                            mixed:     { label: 'part certified', c: '#B45309', t: 'Some of this GPO\'s job-work orders are certified and some are not — the rest is the order estimate.' },
                            estimated: { label: 'estimated', c: '#B45309', t: 'No certified job-work invoice for this GPO. This is qty x rate from the order, not a figure finance has checked.' },
                            none:      { label: '—', c: '#aaa', t: 'No job-work order on this GPO.' },
                          };
                          const jwB = JW_BASIS[r.jobwork_basis] || JW_BASIS.none;
                          const rows = [['Fabric', r.est.fabric, r.actual.fabric], ['Yarn', r.est.yarn, r.actual.yarn], ['Trims', r.est.trims, r.actual.trims],
                            ['Job Work', r.est.jobwork || 0, r.actual.jobwork || 0, jwB], ['Labour', r.est.labour, r.actual.labour], ['Overhead', r.est.overhead, r.actual.overhead]];
                          return (
                            <React.Fragment key={i}>
                              <tr style={{ borderBottom: '1px solid #f5f5f5', cursor: 'pointer', background: open ? '#fafafe' : 'white' }} onClick={() => setExpandedGpo(open ? null : r.gpo_no)}>
                                <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{r.gpo_no}{r.has_actuals && !r.actuals_complete && <span title="using estimated labour/overhead — actuals not yet entered, margin not yet trued to actual" style={{ marginLeft: 6, color: '#854F0B', cursor: 'help' }}>⚠</span>}</td>
                                <td style={{ padding: '10px 12px', fontWeight: 600 }}>{r.style_no || '—'}</td>
                                <td style={{ padding: '10px 12px' }}>{r.buyer || r.brand || '—'}</td>
                                <td style={{ padding: '10px 12px' }}>{Number(r.order_qty).toLocaleString('en-IN')}</td>
                                <td style={{ padding: '10px 12px' }}>{r.has_estimate ? inr(r.est.total) : <span style={{ color: '#ccc' }}>no CE</span>}</td>
                                <td style={{ padding: '10px 12px', fontWeight: 600 }}>{inr(r.actual.total)}</td>
                                <td style={{ padding: '10px 12px', fontWeight: 600, color: varColor(r.variance) }}>{r.variance > 0 ? '+' : ''}{inr(r.variance)}</td>
                                <td style={{ padding: '10px 12px' }}><span style={{ fontWeight: 700, color: marginColor(r.margin_pct) }}>{r.margin_pct == null ? '—' : r.margin_pct.toFixed(1) + '%'}</span></td>
                                <td style={{ padding: '10px 12px', color: '#aaa' }}>{open ? '▲' : '▼'}</td>
                              </tr>
                              {open && (
                                <tr style={{ background: '#fafafe' }}>
                                  <td colSpan={9} style={{ padding: '6px 24px 14px' }}>
                                    <table style={{ width: '60%', borderCollapse: 'collapse', fontSize: 12 }}>
                                      <thead><tr style={{ color: '#888' }}>
                                        <th style={{ textAlign: 'left', padding: '6px 10px' }}>Component</th>
                                        <th style={{ textAlign: 'right', padding: '6px 10px' }}>Estimated</th>
                                        {/* RULING 26b: the header says what an "actual" is here, because
                                            for job work it is EITHER a certified figure OR the order's
                                            own estimate, and the two are indistinguishable as numbers. */}
                                        <th style={{ textAlign: 'right', padding: '6px 10px' }}>Actual
                                          <span style={{ fontWeight: 400, color: '#888' }} title="Job Work is CERTIFIED job-work cost where finance has certified the invoice; otherwise it is the order's qty x rate estimate. Each row says which.">
                                            {' '}(job work: certified where certified)
                                          </span>
                                        </th>
                                        <th style={{ textAlign: 'right', padding: '6px 10px' }}>Variance</th>
                                      </tr></thead>
                                      <tbody>
                                        {rows.map(([label, e, a, basis], j) => (
                                          <tr key={j} style={{ borderTop: '1px solid #eee' }}>
                                            <td style={{ padding: '6px 10px', fontWeight: 600, color: '#444' }}>{label}{label === 'Labour' || label === 'Overhead' ? <span style={{ color: '#aaa', fontWeight: 400 }}> (est)</span> : null}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'right' }}>{inr(e)}</td>
                                            <td style={{ padding: '6px 10px', textAlign: 'right' }}>{inr(a)}
                                              {/* A GPO reading low must be visibly UNCERTIFIED, not silently cheap. */}
                                              {basis && <span title={basis.t} style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: basis.c, cursor: 'help' }}>{basis.label}</span>}
                                            </td>
                                            <td style={{ padding: '6px 10px', textAlign: 'right', color: varColor(a - e), fontWeight: 600 }}>{a - e > 0 ? '+' : ''}{inr(a - e)}</td>
                                          </tr>
                                        ))}
                                        <tr style={{ borderTop: '2px solid #ddd', fontWeight: 700 }}>
                                          <td style={{ padding: '6px 10px' }}>Total · Revenue {inr(r.revenue)}</td>
                                          <td style={{ padding: '6px 10px', textAlign: 'right' }}>{inr(r.est.total)}</td>
                                          <td style={{ padding: '6px 10px', textAlign: 'right' }}>{inr(r.actual.total)}</td>
                                          <td style={{ padding: '6px 10px', textAlign: 'right', color: varColor(r.variance) }}>{r.variance > 0 ? '+' : ''}{inr(r.variance)}</td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                    </tbody>
                    {gpoProfit.length > 0 && (
                      <tfoot><tr style={{ borderTop: '2px solid #ddd', fontWeight: 700, background: '#F7F6F3' }}>
                        <td colSpan={4} style={{ padding: '10px 12px' }}>TOTALS</td>
                        <td style={{ padding: '10px 12px' }}>{inr(gpoTotals.est)}</td>
                        <td style={{ padding: '10px 12px' }}>{inr(gpoTotals.actual)}</td>
                        <td style={{ padding: '10px 12px', color: varColor(gpoTotals.variance) }}>{gpoTotals.variance > 0 ? '+' : ''}{inr(gpoTotals.variance)}</td>
                        <td style={{ padding: '10px 12px', color: marginColor(gpoTotals.margin_pct) }}>{gpoTotals.margin_pct == null ? '—' : Number(gpoTotals.margin_pct).toFixed(1) + '%'}</td>
                        <td></td>
                      </tr></tfoot>
                    )}
                  </table>
                </div>
              );
            })()}

            {tab === 'overview' && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F7F6F3' }}>
                    {['Order', 'Buyer', 'Style', 'Revenue', 'Direct cost', 'Contribution', 'Contribution %', 'Cost basis', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#888', fontWeight: 500, borderBottom: '1px solid #eee' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => {
                    const mb = marginBadge(order.gross_margin_pct);
                    return (
                      <tr key={order.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '10px 12px', color: '#3B3A8F', fontWeight: 500 }}>{order.order_no}</td>
                        <td style={{ padding: '10px 12px' }}>{order.buyer_name}</td>
                        <td style={{ padding: '10px 12px', color: '#666' }}>{order.style_no}</td>
                        <td style={{ padding: '10px 12px', fontWeight: 500 }}>{fmt(order.revenue)}</td>
                        <td style={{ padding: '10px 12px', color: '#E24B4A' }}>{fmtOrBlank(order.total_cogs)}</td>
                        <td style={{ padding: '10px 12px', fontWeight: 500, color: '#E22D2D' }}>{fmtOrBlank(order.gross_profit)}</td>
                        <td style={{ padding: '10px 12px' }}>
                          {order.gross_margin_pct === null || order.gross_margin_pct === undefined ? DASH : (
                            <span style={{ ...mb, padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                              {order.gross_margin_pct}%
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '10px 12px' }}>{basisChip(order.cogs_basis, order)}</td>
                        <td style={{ padding: '10px 12px' }}>{statusBadge(order.status)}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <button onClick={() => {
                            setSelectedOrder(order);
                            setForm({
                              // `?? ''` because these are now NULL on an uncosted order rather
                              // than 0 — a null in a controlled input makes React warn and the
                              // field uneditable.
                              fabric_cost: order.fabric_cost ?? '',
                              trim_cost: order.trim_cost ?? '',
                              cmt_cost: order.cmt_cost ?? '',
                              overhead_cost: order.overhead_cost ?? '',
                              freight_cost: order.freight_cost ?? '',
                              rejection_loss: order.rejection_loss ?? ''
                            });
                            setError('');
                            setShowEditCost(true);
                          }} style={{ padding: '4px 10px', background: '#EEEDFE', color: '#3C3489', border: 'none', borderRadius: 5, fontSize: 11, cursor: 'pointer' }}>
                            Edit Costs
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {tab === 'buyers' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                {buyers.map(buyer => {
                  const mb = marginBadge(buyer.gross_margin_pct);
                  const maxRevenue = Math.max(...buyers.map(b => parseFloat(b.total_revenue || 0)));
                  const barWidth = maxRevenue > 0 ? parseFloat(buyer.total_revenue) / maxRevenue * 100 : 0;
                  return (
                    <div key={buyer.id} style={{ background: '#F7F6F3', borderRadius: 10, padding: 16, border: '1px solid #eee' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>{buyer.buyer_name}</div>
                        {buyer.gross_margin_pct === null || buyer.gross_margin_pct === undefined
                          ? <span style={{ background: '#F4F4F2', color: '#8A6B00', padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}
                              title={`None of this buyer's ${buyer.total_orders} orders carries a recorded cost, so no margin can be computed.`}>
                              no costed orders
                            </span>
                          : <span style={{ ...mb, padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600 }}>{buyer.gross_margin_pct}%</span>}
                      </div>
                      <div style={{ height: 6, background: '#e0e0e0', borderRadius: 3, marginBottom: 10 }}>
                        <div style={{ height: '100%', width: barWidth + '%', background: '#E22D2D', borderRadius: 3 }}></div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                        <div><div style={{ color: '#888', marginBottom: 2 }}>Revenue</div><div style={{ fontWeight: 600 }}>{fmt(buyer.total_revenue)}</div></div>
                        <div><div style={{ color: '#888', marginBottom: 2 }}>Contribution</div><div style={{ fontWeight: 600, color: '#E22D2D' }}>{fmtOrBlank(buyer.gross_profit)}</div></div>
                        <div><div style={{ color: '#888', marginBottom: 2 }}>COGS</div><div style={{ fontWeight: 600, color: '#E24B4A' }}>{fmtOrBlank(buyer.total_cogs)}</div></div>
                        {/* HOW MANY ORDERS THE MARGIN ABOVE SPEAKS FOR (ruling 30). A buyer total
                            is meaningless without it: one costed order among forty reads as the
                            buyer's margin. */}
                        <div><div style={{ color: '#888', marginBottom: 2 }}>Costed</div>
                          <div style={{ fontWeight: 600, color: Number(buyer.costed_orders) ? '#2A2970' : '#8A6B00' }}>
                            {buyer.costed_orders ?? 0} of {buyer.total_orders}
                          </div></div>
                        <div><div style={{ color: '#888', marginBottom: 2 }}>Orders</div><div style={{ fontWeight: 600 }}>{buyer.total_orders}</div></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* NO COSTED SHEET MEANS NO COST MIX (ruling 30). The endpoint now filters to sheets
                carrying a positive cost, so an empty result is a real state and gets said out
                loud — six 0% bars beside a Rs 0 total read as a cost structure that happens to
                be unremarkable, which is the opposite of the truth. */}
            {tab === 'costs' && (() => {
              const buckets = costStructure.buckets || [];
              const unavailable = costStructure.unavailable || [];
              // ── NO COSTED ORDER MEANS NO COST MIX ────────────────────────────────────────
              if (!buckets.length) return (
                <div style={{ border: '1px solid #f6d195', background: '#FFFDF5', borderRadius: 8, padding: '16px 18px' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#854F0B', marginBottom: 6 }}>
                    No order has a derivable direct cost, so there is no breakdown to show
                  </div>
                  <div style={{ fontSize: 13, color: '#6b5a2e', lineHeight: 1.6 }}>
                    {summary.orders_derived_none ?? 0} order(s) have nothing purchased against them and
                    nothing keyed. A 0% share would not mean fabric was free.
                  </div>
                </div>
              );
              return (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  <div>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: '#222', marginBottom: 4 }}>Direct cost breakdown (% of direct cost)</h3>
                    {/* WHAT THIS IS A SHARE OF, BEFORE THE SHARES (ruling 31). */}
                    <div style={{ fontSize: 11.5, color: '#888', marginBottom: 14 }}>
                      Across {costStructure.costed_orders} of {costStructure.total_orders} orders.
                      {!!Number(costStructure.keyed_orders_excluded) && <>
                        {' '}{costStructure.keyed_orders_excluded} hand-keyed order(s) are excluded —
                        a keyed total has no component breakdown to split.
                      </>}
                    </div>
                    {buckets.map((b, i) => (
                      <div key={b.key} style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                          <span style={{ color: '#555' }} title={'Source: ' + b.source}>{b.label}</span>
                          <span style={{ fontWeight: 600 }}>{pctOrBlank(b.pct)} — {fmtOrBlank(b.value)}</span>
                        </div>
                        <div style={{ height: 8, background: '#f0f0f0', borderRadius: 4 }}>
                          <div style={{ height: '100%', width: Math.max(0, Math.min(100, Number(b.pct) || 0)) + '%', background: ['#E22D2D','#378ADD','#534AB7','#EF9F27','#D85A30'][i % 5], borderRadius: 4 }}></div>
                        </div>
                      </div>
                    ))}
                    {/* ── WHAT THIS SPLIT CANNOT SHOW, NAMED (ruling 31) ──────────────────────
                        These used to appear as 0% bars off the hand-keyed table, which claimed
                        their share was nil rather than unknown. */}
                    {!!unavailable.length && (
                      <div style={{ marginTop: 18, borderTop: '1px solid #eee', paddingTop: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#8A6B00', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                          Not in this figure — not shown, and not zero
                        </div>
                        {unavailable.map(u => (
                          <div key={u.key} style={{ fontSize: 12, color: '#6b5a2e', marginBottom: 4 }}>
                            <strong>{u.label}</strong> — {u.why}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 style={{ fontSize: 14, fontWeight: 600, color: '#222', marginBottom: 16 }}>Total cost summary</h3>
                    {[
                      { label: 'Total Revenue (all orders)', value: fmt(summary.total_revenue), color: '#E22D2D', bold: true },
                      { label: `Revenue of orders with a cost`, value: fmtOrBlank(summary.costed_revenue), color: '#555' },
                      { label: `Direct cost (${costStructure.costed_orders ?? 0} order(s))`, value: fmtOrBlank(costStructure.total_cogs), color: '#E24B4A' },
                      ...buckets.map(b => ({ label: '  — ' + b.label, value: fmtOrBlank(b.value), color: '#555' })),
                      { label: 'Contribution after direct cost', value: fmtOrBlank(summary.gross_profit), color: '#3B3A8F', bold: true },
                      { label: 'Contribution %', value: pctOrBlank(summary.gross_margin_pct), color: parseFloat(summary.gross_margin_pct) >= 20 ? '#E22D2D' : '#E24B4A', bold: true },
                    ].map((item, i, arr) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < arr.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                        <span style={{ fontSize: 13, color: '#555' }}>{item.label}</span>
                        <span style={{ fontSize: 13, fontWeight: item.bold ? 700 : 500, color: item.color }}>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

          </div>
        </div>
      </div>

      {showEditCost && selectedOrder && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, width: 520 }}>
            <div style={{ background: '#2D2C72', margin: '-28px -28px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>Edit Cost Sheet</h3></div>
            <p style={{ fontSize: 13, color: '#888', margin: '0 0 20px' }}>
              {selectedOrder.order_no} — {selectedOrder.buyer_name} · Revenue: {fmt(selectedOrder.revenue)}
            </p>
            {error && (
              <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
                {error}
              </div>
            )}
            <form onSubmit={handleEditCost}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[
                  { key: 'fabric_cost', label: 'Fabric Cost (₹)' },
                  { key: 'trim_cost', label: 'Trim Cost (₹)' },
                  { key: 'cmt_cost', label: 'CMT Labour (₹)' },
                  { key: 'overhead_cost', label: 'Overhead (₹)' },
                  { key: 'freight_cost', label: 'Freight (₹)' },
                  { key: 'rejection_loss', label: 'Rejection Loss (₹)' },
                ].map(field => (
                  <div key={field.key}>
                    <label style={labelStyle}>{field.label}</label>
                    <input
                      style={inputStyle}
                      type="number"
                      step="0.01"
                      defaultValue={selectedOrder[field.key] == null ? '' : parseFloat(selectedOrder[field.key]).toFixed(2)}
                      onChange={e => setForm({ ...form, [field.key]: e.target.value })}
                    />
                  </div>
                ))}
              </div>
              {/* ── WHAT IS BEING OVERRIDDEN, AND WHY IT MUST BE JUSTIFIED (ruling 31) ─────────
                  Cost is derived from this order's purchase orders and certified job work. A
                  figure typed here WINS over that, so it asserts the purchase orders are wrong
                  about this order — sometimes correct, never silent. */}
              <div style={{ marginTop: 16, border: '1px solid #e8d5ef', background: '#FBF6FD', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 12, color: '#6A2878', lineHeight: 1.6, marginBottom: 8 }}>
                  This order's cost is normally <strong>derived</strong> from its purchase orders and
                  certified job work
                  {selectedOrder.cogs_basis && selectedOrder.cogs_basis.startsWith('derived') && selectedOrder.total_cogs != null
                    ? <> — currently <strong>{fmt(selectedOrder.total_cogs)}</strong></> : null}.
                  A figure keyed here replaces it.
                </div>
                <label style={labelStyle}>Why is the derived cost wrong? (required)</label>
                <input
                  style={inputStyle}
                  type="text"
                  placeholder="e.g. fabric for this order was issued from stock bought on GPO 1802, so no PO exists against it"
                  defaultValue={selectedOrder.override_reason || ''}
                  onChange={e => setForm({ ...form, override_reason: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowEditCost(false)} style={{ padding: '8px 18px', background: '#f0f2f8', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ padding: '8px 18px', background: '#E22D2D', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
                  {saving ? 'Saving...' : 'Save Costs'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default function Profitability() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => { await logout(); navigate('/login'); };
  return (
    <div style={{ minHeight: '100vh', background: 'transparent', fontFamily: 'inherit' }}>
      
      <div style={{ padding: 24 }}><ProfitabilityContent /></div>
    </div>
  );
}